import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Button, Row, Modal, ModalBody } from "reactstrap";
import moment from "moment";
import ModalCreateEditAction from "./ModalCreateEditAction";
import InactiveModal from "./InactiveModal";
import actionObjectiveActionPlansService from "services/ActionObjectivesActionPlansService";
import DynamicMaterialTableWithNoFilters from "components/Common/MaterialTableContainerWithNoFilters";
import useTableZoom from "components/Common/useTableZoom";
import { PencilSimple, Plus, Trash, Check, SignOut } from "@phosphor-icons/react";
import { IconButton, Tooltip } from "@mui/material";
import ModalCreateEditObjective from "./ModalCreateEditObjective";
import objectiveActionPlansService from "services/ObjectiveActionPlansService";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const status = [
	{
		value: "ANDAMENTO",
		label: "Em Andamento",
		icon: "bx bx-right-arrow",
		color: "primary",
	},
	{
		value: "STANDBY",
		label: "Standby",
		icon: "stop-circle",
		color: "bg-dark",
	},
	{ value: "OK", label: "Ok", icon: "like", color: "success" },
	{ value: "CANCELADO", label: "Cancelado", icon: "block", color: "danger" },
];

const Actions = ({ obj_actions, getObjetivos, planCollaborators }) => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		action: { min: 120 },                    // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		deadline: { min: 90, max: 90 },          // Largura mín/máx da coluna "Prazo" (em pixels)
		"responsible.lastname": { min: 110, max: 110 }, // Largura mín/máx da coluna "Responsável" (em pixels)
		cost: { min: 110, max: 110 },             // Largura mín/máx da coluna "Custo R$" (em pixels)
		status: { min: 110, max: 110 },          // Largura mín/máx da coluna "Status" (em pixels)
		actions: { min: 50, max: 50 },           // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [action, setAction] = useState("");
	const [openModal, setOpenModal] = useState(false);
	const [openModal2, setOpenModal2] = useState(false);
	const [activePerfilModal, setactivePerfilModal] = useState(null);

	const [openEditModal, setOpenEditModal] = useState(false);
	const handleCloseEditModal = () => setOpenEditModal(false);
	const [selectedObjective, setSelectedObjective] = useState(null);
	const handleOpenEditModal = (objective) => {
		setSelectedObjective(objective);
		setOpenEditModal(true);
	};

	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const handleOpenDeleteModal = (objective) => {
		setSelectedObjective(objective);
		setOpenDeleteModal(true);
	};
	const handleCloseDeleteModal = () => setOpenDeleteModal(false);
	const handleConfirmDelete = async () => {
		if (selectedObjective) {
			try {
				await objectiveActionPlansService.put(selectedObjective.id, {
					active: 0,
				});
				if (getObjetivos) {
					getObjetivos();
				}
			} catch (error) {
				console.error(error);
			}
		}
		setOpenDeleteModal(false);
	};

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	document.title = "Senda | Ações";

	const columns = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "action",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('action'),
				minWidthPixels: COLUMN_WIDTHS.action.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ cell }) => {
					const actionName = cell.getValue();
					return (
						<span
							style={{
								cursor: "pointer",
								display: "block", // Permite que o texto quebre corretamente
								maxWidth: "100%", // Respeita o tamanho da célula
								wordBreak: "break-word", // Quebra o texto se for muito grande
								overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
								padding: "3px",
							}}
							onMouseEnter={(e) => {
								e.target.style.textDecoration = "underline";
							}}
							onMouseLeave={(e) => {
								e.target.style.textDecoration = "none";
							}}
							onClick={() => setActionModal(cell.row.original)}
						>
							{actionName}
						</span>
					);
				},
			},
			{
				header: "Prazo",
				accessorKey: "deadline",
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('deadline'),
				minWidthPixels: COLUMN_WIDTHS.deadline.min,
				maxWidthPixels: COLUMN_WIDTHS.deadline.max,
				Cell: ({ cell }) => {
					const deadlineDate = moment(cell.getValue());
					const today = moment();
					const rowData = cell.row.original;
					const status = rowData.status;
					
					let cellStyle = {
						padding: "3.5px",
						textAlign: "center",
						width: "100%",
					};

					const isPastDeadline = deadlineDate.isBefore(today, "day");
					if (isPastDeadline && status === "ANDAMENTO") {
						cellStyle = {
							...cellStyle,
							color: "white",
							backgroundColor: "red",
						};
					} else if (isPastDeadline && status === "STANDBY") {
						cellStyle = {
							...cellStyle,
							color: "#000",
							backgroundColor: "#f1b44c",
						};
					}

					return (
						<div style={cellStyle}>
							{deadlineDate.format("DD/MM/YYYY")}
						</div>
					);
				},
			},
			{
				header: "Responsável",
				accessorKey: "responsible.lastname",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('responsible.lastname'),
				minWidthPixels: COLUMN_WIDTHS["responsible.lastname"].min,
				maxWidthPixels: COLUMN_WIDTHS["responsible.lastname"].max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Custo R$",
				accessorKey: "cost",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('cost'),
				minWidthPixels: COLUMN_WIDTHS.cost.min,
				maxWidthPixels: COLUMN_WIDTHS.cost.max,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					return value
						? new Intl.NumberFormat("pt-BR", {
								style: "currency",
								currency: "BRL",
						  }).format(value)
						: "R$ 0,00"; // Caso o valor seja null ou undefined
				},
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Status",
				accessorKey: "status",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('status'),
				minWidthPixels: COLUMN_WIDTHS.status.min,
				maxWidthPixels: COLUMN_WIDTHS.status.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				accessorKey: "actions",
				widthPixels: getColumnWidth('actions'),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: (row) => (
					<div style={{ 
						display: "flex", 
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: 5,
					}}>
						{/* <div style={{ marginRight: 10 }}>
							<Tooltip title="Editar" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: 0,
										cursor: "pointer",
										color: "black",
									}}
									onClick={() =>
										setActionModal(row.row.original)
									}
								>
									<PencilSimple size={16} />
								</IconButton>
							</Tooltip>
						</div> */}
						<Tooltip 
							title={row.row.original.status === "OK" ? "Status já marcado como OK" : "Marcar status como OK"} 
							placement="top"
						>
							<span>
								<IconButton
									disabled={row.row.original.status === "OK"}
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: row.row.original.status === "OK" ? "not-allowed" : "pointer",
										color: row.row.original.status === "OK" ? "#cccccc" : "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() =>
										handleOpenMarkAsOkModal(row.row.original)
									}
								>
									<Check size={14} />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Excluir" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									excluirItem(row.row.original)
								}
							>
								<Trash size={14} />
							</IconButton>
						</Tooltip>
					</div>
				),
				align: "center",
			},
		],
		[zoomLevel], // Adicionado zoomLevel como dependência
	);

	const setActionModal = (rowData) => {
		setAction(rowData);
		setOpenModal(true);
	};

	function onCloseModal() {
		setOpenModal(false);
	}

	function onCloseModal2() {
		setOpenModal2(false);
	}
	const excluirItem = (rowData) => {
		setAction(rowData);
		setOpenModal2(true);
	};

	const [openMarkAsOkModal, setOpenMarkAsOkModal] = useState(false);
	const [selectedAction, setSelectedAction] = useState(null);
	const handleOpenMarkAsOkModal = (actionData) => {
		setSelectedAction(actionData);
		setOpenMarkAsOkModal(true);
	};
	const handleCloseMarkAsOkModal = () => setOpenMarkAsOkModal(false);
	const handleConfirmMarkAsOk = async () => {
		if (selectedAction) {
			try {
				const response = await actionObjectiveActionPlansService.put(
					selectedAction.id,
					{ status: "OK" },
				);
				setData(response);
				if (getObjetivos) {
					getObjetivos();
				}
			} catch (error) {
				console.error(error);
			}
		}
		setOpenMarkAsOkModal(false);
	};

	const inactivate = async () => {
		try {
			if (action) {
				const response = await actionObjectiveActionPlansService.put(
					action.id,
					{ active: 0 },
				);
				setData(response);
			}
		} catch (error) {
			console.log(error);
		}
		setOpenModal2(false);
		setactivePerfilModal(false);
	};

	function onDelete() {
		if (activePerfilModal) {
			setactivePerfilModal(false);
		} else {
			setactivePerfilModal(true);
		}
	}

	useEffect(() => {
		if (obj_actions?.actions) {
			// Ordena os dados por prazo (deadline) em ordem crescente
			const sortedData = [...obj_actions.actions].sort((a, b) => {
				const dateA = new Date(a.deadline);
				const dateB = new Date(b.deadline);
				return dateA - dateB;
			});
			setData(sortedData);
		}
	}, [obj_actions]);

	useEffect(() => {
		inactivate();
	}, [activePerfilModal]);

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					alignItems: "center",
					marginBottom: 20,
					marginTop: 10,
					gap: 5,
				}}
			>
				<Button
					onClick={() => setActionModal({})}
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						height: 30,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<Plus size={16} color="black" /> Nova Ação
				</Button>

				<Button
					onClick={() => handleOpenEditModal(obj_actions)}
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						height: 30,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<PencilSimple size={16} /> Editar Objetivo
				</Button>
				<Button
					onClick={() => handleOpenDeleteModal(obj_actions)}
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						height: 30,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<Trash size={16} /> Excluir Objetivo
				</Button>
			</div>

			<div style={{ marginBottom: 60 }}>
				<DynamicMaterialTableWithNoFilters
					columns={columns}
					data={data}
				/>
			</div>

			<InactiveModal
				show={openModal2}
				onCloseClick={onCloseModal2}
				onDeleteClick={onDelete}
				nomePerfil={action?.action}
			/>

			<ModalCreateEditAction
				getObjetivos={getObjetivos}
				show={openModal}
				onCloseClick={onCloseModal}
				data={action}
				objective_id={obj_actions.id}
				objective_deadline={obj_actions.deadline}
				setData={setData}
				allowedResponsibleUserIds={planCollaborators}
			/>

			<ModalCreateEditObjective
				show={openEditModal}
				getObjetivos={getObjetivos}
				onCloseClick={handleCloseEditModal}
				data={selectedObjective}
				action_plans_id={selectedObjective?.action_plans_id}
				allowedResponsibleUserIds={planCollaborators}
			/>

			<InactiveModal
				show={openDeleteModal}
				onCloseClick={handleCloseDeleteModal}
				onDeleteClick={handleConfirmDelete}
				nomePerfil={selectedObjective?.objective}
			/>

			<Modal
				size="m"
				isOpen={openMarkAsOkModal}
				toggle={handleCloseMarkAsOkModal}
				centered={true}
			>
				<div className="modal-content">
					<ModalBody className="px-4 py-5 text-center">
						<p className="text-muted font-size-16 mb-4">
							Você tem certeza que deseja mudar o status para OK?
						</p>
						<p className="text-muted font-size-16 mb-4 fw-bold">
							{selectedAction?.action}
						</p>

						<div className="hstack gap-2 justify-content-center mb-0">
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								onClick={handleConfirmMarkAsOk}
							>
								<Check size={16} /> Confirmar
							</Button>
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								onClick={handleCloseMarkAsOkModal}
							>
								<SignOut size={16} /> Fechar
							</Button>
						</div>
					</ModalBody>
				</div>
			</Modal>
		</>
	);
};
Actions.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(Actions);
